/**
 * @prettier
 */

import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withTranslation } from "react-i18next";
import { history } from "../../state/store.js";

// Actions
import {
    decrementSidesLeft,
    setCardOrientation,
    setCardType,
} from "../../state/actions/idPropertiesActions.js";

import {
    setInstanceID,
    submitBackID,
    submitFrontID,
} from "../../state/actions/configActions";

// Services
import { isIdPalCameraEnabled } from "../../services/shared/helpers.js";

// Components
import Header from "../../components/Header.jsx";
import CustomButton from "../../components/CustomButton";
import Navigator from "../../components/Navigator";
import OutOfRecaptures from "../../components/Messages/OutOfRecaptures";
import SubmissionFlowStepper from "../../components/SubmissionFlowStepper/index.jsx";

// Images
import dlFrontJpg from "../../assets/img/dl_card_front.jpg";
import dlBackJpg from "../../assets/img/dl_card_back.jpg";
import idCardBackJpg from "../../assets/img/id_card_back.jpg";
import idCardFrontJpg from "../../assets/img/id_card_back.jpg";

// Config
import { ACTION_LABELS } from "../../config/dataDogActionLabels.js";

const ACTION = ACTION_LABELS.capturePhoto;

class CapturePhoto extends Component {
    constructor(props) {
        super(props);
        this.state = {
            inputValue: null,
            navigation: {
                action: "load",
                props: null,
            },
        };
    }

    componentDidMount() {
        if (
            this.props.submissionAttempts.remaining <= 0 &&
            this.props.sidesLeft === 0 &&
            this.props.submissionAttempts[
                this.props.sidesLeft === 2 ? "front" : "back"
            ] > 0
        ) {
            return;
        } else if (this.props.location && this.props.location.state) {
            if (this.props.location.state.isRetry) {
                this.navigateCamera();
            }
        } else {
            return;
        }

        // Sets document title
        const { t } = this.props;
        document.title = t("idpal_doc_title_capture");
    }

    getOrientationCopy() {
        const { t } = this.props;
        return this.props.orientation === 0 ? t("xxx_front") : t("xxx_back");
    }

    getCardTypeCopy() {
        const { t } = this.props;

        switch (this.props.cardType) {
            case 1:
                return t("xxx_id_card"); // passport
            case 2:
                return "medical card";
            case 3:
                return t("idpal_national_id"); // new for id's licence
            case 31:
                return t("idpal_driving_licence"); // new for id's licence
            case 32:
                return t("idpal_resident_card"); // new for id's licence
            default:
                return t("xxx_id_card");
        }
    }

    openCamera(type) {
        this.props.setCardType(type);
        this.navigateCamera();
    }

    getBackImage(cardType) {
        switch (cardType) {
            case 4:
                return idCardBackJpg;
            case 31:
                return dlBackJpg;
            default:
                return idCardBackJpg;
        }
    }

    getFrontImage(cardType) {
        switch (cardType) {
            case 4:
                return idCardFrontJpg;
            case 31:
                return dlFrontJpg;
            default:
                return idCardFrontJpg;
        }
    }

    navigateCamera() {
        this.setState({
            navigation: {
                action: "next",
                props: {
                    documentId: this.props.location.state.documentId,
                    isIdPalCamera:
                        isIdPalCameraEnabled(this.props.captureMethod) ||
                        this.props.bypassAcuant,
                },
            },
        });
    }

    render() {
        const { t } = this.props;

        // Out of retries, and we've already tried this side
        if (
            this.props.submissionAttempts.remaining <= 0 &&
            this.props.sidesLeft === 0 &&
            this.props.submissionAttempts[
                this.props.sidesLeft === 2 ? "front" : "back"
            ] > 0
        ) {
            return (
                <Fragment>
                    <Header />
                    <OutOfRecaptures
                        continue={() => {
                            history.go(2);
                        }}
                        message="idpal_out_of_attempts"
                    />
                    <Navigator
                        page={"document_capture"}
                        action={this.state.navigation.action}
                        propsToPass={this.state.navigation.props}
                    />
                </Fragment>
            );
        }

        return (
            <Fragment>
                <div className="root-layout-container instructions-page">
                    <Header />
                    <SubmissionFlowStepper />
                    <div className="instructions-title">
                        {t("xxx_upload_clear_picture")}{" "}
                        {this.getOrientationCopy()} {t("xxx_of_your")}{" "}
                        {this.getCardTypeCopy()}.
                    </div>

                    <div className="u-display-upload u-text-center">
                        {this.props.sidesLeft === 1 && (
                            <img
                                alt="Illustration of an Id"
                                className={"capture"}
                                src={
                                    this.props.frontSubmitted
                                        ? this.getFrontImage(
                                              this.props.cardType
                                          )
                                        : this.getBackImage(this.props.cardType)
                                }
                            />
                        )}
                    </div>

                    {this.props.sidesLeft === 1 && (
                        <CustomButton
                            className="btn"
                            handleClick={() =>
                                this.openCamera(this.props.cardType)
                            }
                            label={t("idpal_continue")}
                            actionDataLabel={ACTION.continueButton}
                        />
                    )}
                </div>

                <Navigator
                    page={"capture_photo"}
                    action={this.state.navigation.action}
                    propsToPass={this.state.navigation.props}
                />
            </Fragment>
        );
    }
}

function mapStateToProps(state) {
    return {
        instanceID: state.config.instanceID,
        bypassAcuant: state.config.bypassAcuant,
        orientation: state.idProperties.orientation,
        cardType: state.idProperties.cardType,
        sidesLeft: state.idProperties.sidesLeft,
        frontSubmitted: state.config.frontSubmitted,
        backSubmitted: state.config.backSubmitted,
        submissionAttempts: state.submissionState.submissionAttempts,
        captureMethod:
            state.submissionState.screens.document_upload.capture_method,
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            setCardType,
            setInstanceID,
            setCardOrientation,
            decrementSidesLeft,
            submitFrontID,
            submitBackID,
        },
        dispatch
    );
}

export default withTranslation("translation")(
    connect(mapStateToProps, mapDispatchToProps)(CapturePhoto)
);
